// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides React Query hooks for the organization's
 * needs-attention threshold settings.
 */
import {
  getAttentionThresholds,
  updateAttentionThresholds,
} from '@/account/api/attention-thresholds';
import type { UpdateAttentionThresholdsInput } from '@/account/api/types';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';

/**
 * Hook for fetching organization attention threshold settings with suspense
 * support.
 *
 * @param orgKey - The unique key identifying the organization
 * @returns Query result containing the threshold settings
 */
export function useAttentionThresholds(orgKey: string) {
  return useSuspenseQuery({
    queryKey: ['organization', orgKey, 'attention-thresholds'],
    queryFn: () => getAttentionThresholds(orgKey),
  });
}

/**
 * Hook for replacing the organization's attention threshold override set.
 * Invalidates the threshold settings and shipment caches on success, since
 * threshold changes retroactively change which shipments need attention.
 *
 * @returns Mutation object with mutate function and state
 */
export function useUpdateAttentionThresholds() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgKey,
      input,
    }: {
      orgKey: string;
      input: UpdateAttentionThresholdsInput;
    }) => updateAttentionThresholds(orgKey, input),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['organization', variables.orgKey, 'attention-thresholds'],
      });
      await queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      // Shipment detail views compute attention from the same thresholds;
      // their keys are ['shipment', <key>], so invalidate the prefix
      await queryClient.invalidateQueries({ queryKey: ['shipment'] });
    },
  });
}
